import React, { useCallback, useEffect, useState } from 'react';
import FlowStrip from '../../help/FlowStrip';
import StatusStagesChip from '../../help/StatusStagesChip';
import { salesFlow } from '../../help/flows';
import { RECEIPT_STAGES } from '../../help/statusStages';
import FcAmountLine from '../common/FcAmountLine';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { ReceiptService } from '../../services/receipt.service';
import {
  Receipt, ReceiptEditDTO, ReceiptEditHistory, ReceiptMethod, ReceiptStatus,
} from '../../interfaces/sales/receipt.interface';
import { ReverseConfirmDialog } from '../procurement/ReverseConfirmDialog';
import { EditHistoryPanel, EditHistoryRow } from '../procurement/EditHistoryPanel';
import { AccountService } from '../../services/account.service';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { useToast } from '../../context/ToastContext';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import { SalesInvoice } from '../../interfaces/sales/salesInvoice.interface';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { depositAccountOptions } from '../../utils/postingAccounts';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { saveBlob } from '../../utils/saveBlob';
import ApplyReceiptDialog from './ApplyReceiptDialog';
import AllocationsPanel from '../allocation/AllocationsPanel';
import AttachmentsPanel from '../common/AttachmentsPanel'; // W3-K
import { isCurrentUser } from '../../utils/currentUser';
import { helpWhy } from '../../help/why';

const today = () => toLocalIsoDate();

const RECEIPT_METHODS: { value: ReceiptMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'MOBILE_BANKING', label: 'Mobile Banking' },
  { value: 'OTHER', label: 'Other' },
];

const STATUS_COLORS: Record<ReceiptStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  POSTED: 'success',
  REVERSED: 'error',
};

const mapToEditHistoryRow = (e: ReceiptEditHistory): EditHistoryRow => {
  const parts: string[] = [
    `amount was ${formatMoney(e.prevAmount)}`,
    `date was ${formatDate(e.prevReceiptDate)}`,
    `method was ${humanizeEnum(e.prevMethod)}`,
  ];
  if (e.prevReference) parts.push(`ref was ${e.prevReference}`);
  return {
    editSeq: e.editSeq,
    editedAt: e.editedAt,
    editReason: e.editReason,
    summary: parts.join('; ') || '—',
  };
};

const ReceiptDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.RECEIPT_MANAGE);

  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState<SalesInvoice | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [allocRefresh, setAllocRefresh] = useState(0);
  const [depositAccountName, setDepositAccountName] = useState<string | null>(null);
  const [editHistoryRows, setEditHistoryRows] = useState<EditHistoryRow[]>([]);

  // Reverse state
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [reversing, setReversing] = useState(false);

  // Edit modal state
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assetAccounts, setAssetAccounts] = useState<Account[]>([]);

  // Edit form fields
  const [editAmount, setEditAmount] = useState<number>(NaN);
  const [editDate, setEditDate] = useState(today());
  const [editDepositAccountId, setEditDepositAccountId] = useState('');
  const [editMethod, setEditMethod] = useState<ReceiptMethod>('CASH');
  const [editReference, setEditReference] = useState('');
  const [editReason, setEditReason] = useState('');

  const loadData = useCallback(() => {
    if (!id) return;
    setLoading(true);
    ReceiptService.getById(id)
      .then(r => {
        setReceipt(r);
        return ReceiptService.editHistory(id)
          .then((rows: ReceiptEditHistory[]) => {
            setEditHistoryRows(rows.map(mapToEditHistoryRow));
          })
          .catch(() => setEditHistoryRows([]));
      })
      .catch(e => toast.showApiError(e, 'Failed to load receipt'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load ASSET accounts for the edit modal
  useEffect(() => {
    AccountService.getAccountsByType('ASSET')
      .then((accounts: Account[]) => setAssetAccounts(accounts))
      .catch((e: unknown) => toast.showApiError(e, 'Failed to load accounts'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Show names, not ids: the invoice (number + customer) and the deposit account.
  useEffect(() => {
    if (!receipt) return;
    if (receipt.salesInvoiceId) {
      SalesInvoiceService.getById(receipt.salesInvoiceId)
        .then(setInvoice)
        .catch(() => setInvoice(null));
    } else {
      setInvoice(null);
    }
    AccountService.getAccountById(receipt.depositAccountId)
      .then((a: Account) => setDepositAccountName(`${a.code} - ${a.name}`))
      .catch(() => setDepositAccountName(null));
  }, [receipt?.salesInvoiceId, receipt?.depositAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEditModal = () => {
    if (!receipt) return;
    setEditAmount(receipt.amount);
    setEditDate(receipt.receiptDate);
    setEditDepositAccountId(receipt.depositAccountId);
    setEditMethod(receipt.method);
    setEditReference(receipt.reference ?? '');
    setEditReason('');
    setEditOpen(true);
  };

  const handlePrint = async () => {
    if (!id) return;
    try {
      const blob = await ReceiptService.downloadPdf(id);
      saveBlob(blob, `receipt-${receipt?.receiptNumber ?? id}.pdf`);
    } catch (e) {
      toast.showApiError(e, 'Failed to download receipt');
    }
  };

  const handleReverse = async () => {
    if (!id) return;
    setReversing(true);
    try {
      const updated = await ReceiptService.reverse(id);
      setReceipt(updated);
      toast.showSuccess('Receipt reversed');
      setReverseDialogOpen(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to reverse receipt');
    } finally {
      setReversing(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!id || !editReason.trim()) return;
    setSaving(true);
    try {
      const dto: ReceiptEditDTO = {
        editReason: editReason.trim(),
        amount: editAmount,
        receiptDate: editDate,
        method: editMethod,
        reference: editReference.trim() || undefined,
        depositAccountId: editDepositAccountId,
      };
      await ReceiptService.edit(id, dto);
      toast.showSuccess('Receipt updated');
      setEditOpen(false);
      loadData();
    } catch (e) {
      toast.showApiError(e, 'Failed to update receipt');
      // Keep modal open so user can correct
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!receipt) return <Box sx={{ p: 3 }}><Typography>Receipt not found.</Typography></Box>;
  const unapplied = Math.round((receipt.amount - (receipt.allocatedAmount ?? 0)) * 100) / 100;

  const isReversed = receipt.status === 'REVERSED';
  // B-22, mirrors ReceiptService.assertReverserIsNotMaker (unconditional, no setting): the maker cannot reverse.
  const makerBlock = isCurrentUser(receipt.createdBy) ? 'You created this receipt, so a second person must reverse it (maker and checker must be different users).' : null;
  const isEdited = editHistoryRows.length > 0;

  const editAmountValid = Number.isFinite(editAmount) && editAmount > 0;
  const editSubmitDisabled = saving || !editAmountValid || !editDepositAccountId || !editReason.trim();

  return (
    <Box sx={{ p: 3 }}>
      <FlowStrip data-help="sales.receipt.flow" stages={salesFlow({ current: 'receipt', invoices: [{ id: receipt.salesInvoiceId, no: receipt.invoiceNumber }], receipts: [{ id: receipt.id, no: receipt.receiptNumber ?? 'Receipt' }], journalEntryId: receipt.journalEntryId })} />
      {/* Header row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5">{receipt.receiptNumber ?? '—'}</Typography>
          <StatusStagesChip data-help="sales.receipt.statusStages" label={humanizeEnum(receipt.status)} status={receipt.status} stages={RECEIPT_STAGES} color={STATUS_COLORS[receipt.status]} />
          {isEdited && <Chip label="Edited" color="warning" size="small" />}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button data-help="sales.receipt.print" variant="outlined" onClick={handlePrint}>
            Print receipt
          </Button>
          {canManage && !isReversed && !receipt.salesInvoiceId && unapplied > 0.004 && (
            <Button data-help="accounting.receipt.apply" variant="contained" onClick={() => setApplyOpen(true)}>
              Apply to invoice
            </Button>
          )}
          {canManage && !isReversed && !!receipt.salesInvoiceId && (
            <Button data-help="accounting.receipt.edit" variant="outlined" onClick={openEditModal}>
              Edit
            </Button>
          )}
          {canManage && !isReversed && (
            <Button data-help="accounting.receipt.reverse" {...helpWhy(makerBlock ? { kind: 'custom', message: makerBlock } : null)} disabled={!!makerBlock} variant="outlined" color="error" onClick={() => setReverseDialogOpen(true)}>
              Reverse
            </Button>
          )}
          <Button data-help="sales.receipt.back" variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>

      {/* Detail card */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Receipt Number</Typography>
            <Typography>{receipt.receiptNumber ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            <Chip label={humanizeEnum(receipt.status)} color={STATUS_COLORS[receipt.status]} size="small" />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Sales Invoice</Typography>
            {receipt.salesInvoiceId ? (
              <Typography
                component={RouterLink}
                to={`/sales/invoices/${receipt.salesInvoiceId}`}
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {invoice?.invoiceNumber ?? receipt.invoiceNumber ?? 'View invoice'}
              </Typography>
            ) : (
              <Typography>
                {(receipt.allocatedAmount ?? 0) <= 0.004
                  ? 'On account (unapplied)'
                  : unapplied <= 0.004
                    ? 'Fully applied to invoices'
                    : `Partially applied: ${formatMoney(receipt.allocatedAmount ?? 0)} of ${formatMoney(receipt.amount)}`}
              </Typography>
            )}
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Customer</Typography>
            <Typography>{receipt.customerName ?? invoice?.customerName ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Amount</Typography>
            <Typography>{formatMoney(receipt.amount)}</Typography>
            <FcAmountLine currency={receipt.currencyCode} rate={receipt.exchangeRate} foreign={receipt.foreignAmount}
              bdt={receipt.bdtAmount ?? receipt.amount} fxDifference={receipt.fxDifference} />
          </Box>
          {!receipt.salesInvoiceId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Unapplied balance</Typography>
              <Typography>{formatMoney(unapplied)}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="body2" color="text.secondary">Receipt Date</Typography>
            <Typography>{formatDate(receipt.receiptDate)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Deposit Account</Typography>
            <Typography>{depositAccountName ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Method</Typography>
            <Typography>{humanizeEnum(receipt.method)}</Typography>
          </Box>
          {receipt.reference && (
            <Box>
              <Typography variant="body2" color="text.secondary">Reference</Typography>
              <Typography>{receipt.reference}</Typography>
            </Box>
          )}
          {receipt.journalEntryId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Journal Entry</Typography>
              <Typography
                component={RouterLink}
                to={`/accounting/journal-entries/${receipt.journalEntryId}`}
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                View journal entry
              </Typography>
            </Box>
          )}
          {receipt.reversalJournalEntryId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Reversal Journal Entry</Typography>
              <Typography
                component={RouterLink}
                to={`/accounting/journal-entries/${receipt.reversalJournalEntryId}`}
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                View reversal entry
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {!receipt.salesInvoiceId && (
        <AllocationsPanel creditType="CUSTOMER_RECEIPT" creditId={receipt.id} refreshKey={allocRefresh}
          canReverse={canManage && !isReversed} onChanged={loadData} />
      )}

      <ApplyReceiptDialog
        open={applyOpen}
        receipt={receipt}
        onClose={() => setApplyOpen(false)}
        onApplied={r => { setReceipt(r); setAllocRefresh(k => k + 1); }}
      />

      {/* Edit history panel */}
      <EditHistoryPanel title="Receipt edit history" rows={editHistoryRows} />

      {/* Reverse confirm dialog */}
      <ReverseConfirmDialog
        open={reverseDialogOpen}
        entityLabel="Receipt"
        consequence="Posts a reversing journal entry dated today and re-opens the invoice balance. If you recorded this receipt yourself, a second person must reverse it."
        onCancel={() => setReverseDialogOpen(false)}
        onConfirm={handleReverse}
        loading={reversing}
      />

      {/* Edit modal */}
      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} maxWidth="sm" fullWidth data-help-page="sales.receiptEdit.page">
        <DialogTitle>Edit Receipt</DialogTitle>
        <DialogContent data-help-section="sales.receiptEdit.section.form">
          <Stack spacing={2} sx={{ pt: 1 }}>
            {/* Amount */}
            <TextField
              data-help="sales.receiptEdit.amount"
              label="Amount"
              type="number"
              value={Number.isFinite(editAmount) ? editAmount : ''}
              onChange={(e) => setEditAmount(e.target.value === '' ? NaN : Number(e.target.value))}
              inputProps={{ min: 0, step: 'any' }}
              fullWidth
              required
              error={Number.isFinite(editAmount) && !editAmountValid}
              helperText={Number.isFinite(editAmount) && !editAmountValid ? 'Amount must be greater than 0' : undefined}
            />

            {/* Receipt Date */}
            <TextField
              data-help="sales.receiptEdit.date"
              label="Receipt Date"
              type="date"
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />

            {/* Deposit Account (ASSET accounts) */}
            <AccountAutocomplete
              data-help="sales.receiptEdit.depositAccount"
              accounts={depositAccountOptions(assetAccounts)}
              value={editDepositAccountId}
              onChange={setEditDepositAccountId}
              label="Deposit Account"
              required
            />

            {/* Method */}
            <TextField
              data-help="sales.receiptEdit.method"
              select
              label="Method"
              value={editMethod}
              onChange={(e) => setEditMethod(e.target.value as ReceiptMethod)}
              fullWidth
              required
            >
              {RECEIPT_METHODS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>

            {/* Reference (optional) */}
            <TextField
              data-help="sales.receiptEdit.reference"
              label="Reference (optional)"
              value={editReference}
              onChange={(e) => setEditReference(e.target.value)}
              fullWidth
            />

            {/* Edit Reason (required) */}
            <TextField
              data-help="sales.receiptEdit.reason"
              label="Edit Reason"
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              fullWidth
              required
              multiline
              rows={2}
              error={editReason.trim() === '' && saving}
              helperText="Required — explain why this receipt is being edited"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.receiptEdit.cancel" onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            data-help="sales.receiptEdit.submit"
            onClick={handleEditSubmit}
            variant="contained"
            disabled={editSubmitDisabled}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
      {id && <AttachmentsPanel entityType="RECEIPT" entityId={id} canManage={canManage} />} {/* W3-K */}
    </Box>
  );
};

export default ReceiptDetail;
